import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermission } from "@/contexts/AuthContext";
import { publicLibraryChatPath, regulatoryGlobalChatPath } from "@/apps/regulatory/paths.js";
import {
  createPublicThread,
  createThread,
  createWorkspaceThread,
  listLibraries,
  listPublicLibraries,
} from "../api/rag-api.js";
import { DEFAULT_CHAT_TITLE } from "../constants.js";
import { useRefreshRagNav } from "./RagNavRefreshContext.js";
import type { RagNavCreateChat } from "../rag-nav.js";

export function useSidebarNewChat(activeAppId: string | undefined) {
  const navigate = useNavigate();
  const refreshRagNav = useRefreshRagNav();
  const canCreateKmChat = usePermission("kmChat", "create");
  const [creating, setCreating] = useState(false);

  const canCreateChat =
    activeAppId === "regulatory" ? true : activeAppId === "rag" ? canCreateKmChat : false;

  const createNewChat = useCallback(
    async (config: RagNavCreateChat) => {
      if (!canCreateChat || creating) return;

      setCreating(true);
      try {
        if (config.scope === "library") {
          const { thread } = await createThread(config.libraryId, {
            title: DEFAULT_CHAT_TITLE,
          });
          navigate(
            `/rag/workspaces/${config.workspaceId}/libraries/${config.libraryId}/chats/${thread.id}`
          );
        } else if (config.scope === "workspace") {
          const { libraries } = await listLibraries(config.workspaceId);
          const { thread } = await createWorkspaceThread(config.workspaceId, {
            title: DEFAULT_CHAT_TITLE,
            libraryIds: libraries.map((library) => library.id),
          });
          navigate(`/rag/workspaces/${config.workspaceId}/chat/${thread.id}`);
        } else if (config.scope === "regulatoryGlobal") {
          const { libraries: publicLibraries } = await listPublicLibraries();
          const { thread } = await createPublicThread({
            title: DEFAULT_CHAT_TITLE,
            libraryIds: publicLibraries.map((library) => library.id),
          });
          navigate(regulatoryGlobalChatPath(thread.id));
        } else {
          const { thread } = await createThread(config.libraryId, {
            title: DEFAULT_CHAT_TITLE,
          });
          navigate(publicLibraryChatPath(config.libraryId, thread.id));
        }

        refreshRagNav();
      } finally {
        setCreating(false);
      }
    },
    [canCreateChat, creating, navigate, refreshRagNav]
  );

  return { createNewChat, creating, canCreateChat };
}
